import React from 'react';
import { ActivityIndicator, Alert, KeyboardAvoidingView, Platform, Pressable, ScrollView, StyleSheet, Text, TextInput, View } from 'react-native';
import { useRouter } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { addCondition, replyToCondition, useAccount, useConditions, useCredits } from '../../src/data';
import { KakaoButton } from '../../src/KakaoButton';
import { summarize } from '../../src/format';
import { Head } from '../../src/ui';
import { D } from '../../src/theme';
import type { Condition } from '../../src/types';

/**
 * AI 조건 등록. 이 앱이 폴센트·꿀딜과 다른 유일한 지점이라 앞문이 여기다.
 *
 * 예전 화면은 AI 라고 써 놓고 로컬 정규식(quickParse) 결과를 보여 준 뒤
 * "정확한 해석은 저장 후" 라고 적어 뒀다. 실제 AI 는 5분 뒤에나 돌았기 때문이다.
 * 지금은 수집기가 Firestore 리스너로 3초 안에 해석하고 2초 안에 되묻는다.
 * 그래서 이 화면은 미리보기를 흉내 내지 않고, 진짜 AI 가 답하는 자리를 그대로 보여 준다.
 */
// 가입 전 입력 제한. 서버(DEALS_ANON_TEXT_LIMIT)와 같은 값이어야 한다
const FREE_LIMIT = 30;

const EXAMPLES = [
  { text: '에어팟 프로 2 20만원 아래', hint: '상품을 아는 경우' },
  { text: '5080 16기가 1TB PC 400만원 밑', hint: '스펙으로 거는 경우' },
  { text: '오쏘몰 이뮨 같은 영양제', hint: '설명으로 거는 경우' },
];

export default function Search() {
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const conds = useConditions();
  const credits = useCredits();
  const account = useAccount();

  const [text, setText] = React.useState('');
  const [activeId, setActiveId] = React.useState<string | null>(null);
  const [dismissed, setDismissed] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [reply, setReply] = React.useState('');

  // 탭을 떠났다 와도 AI 가 답을 기다리는 조건이 있으면 그 대화를 이어서 보여 준다.
  // 질문만 던져 놓고 화면이 초기화되면 사용자는 답할 자리를 영영 못 찾는다.
  const pending = conds.find((c) => c.needs_confirm && c.active) ?? null;
  const active = activeId ? conds.find((c) => c.id === activeId) ?? null : dismissed ? null : pending;
  // 가입 전에는 하루 한 번, 짧은 문장만. 서버가 진짜로 막고 여기서는 미리 알려만 준다
  const over = !account.signedUp && text.trim().length > FREE_LIMIT;
  const ready = text.trim().length >= 2 && !over;

  function startNew() {
    setActiveId(null);
    setDismissed(true);
  }

  async function send() {
    if (!ready || busy) return;
    setBusy(true);
    try {
      setActiveId(await addCondition(text.trim()));
      setDismissed(false);
      setText('');
    } catch (e) {
      Alert.alert('저장 실패', e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  function logout() {
    Alert.alert('로그아웃할까요?', '조건과 크레딧은 계정에 남아 있어요. 다시 로그인하면 그대로 보입니다.', [
      { text: '취소', style: 'cancel' },
      {
        text: '로그아웃',
        style: 'destructive',
        onPress: async () => {
          const { signOutFromKakao } = await import('../../src/kakao');
          await signOutFromKakao().catch(() => {});
          setActiveId(null);
          setDismissed(true);
        },
      },
    ]);
  }

  async function answer() {
    const t = reply.trim();
    if (!t || !active) return;
    setReply('');
    await replyToCondition(active.id, t, turnsOf(active)).catch(() => {});
  }

  return (
    <KeyboardAvoidingView style={{ flex: 1, backgroundColor: D.ground }} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
      <ScrollView
        contentContainerStyle={{ paddingHorizontal: 16, paddingTop: insets.top + 16, paddingBottom: 24, gap: 14 }}
        keyboardShouldPersistTaps="handled"
      >
        {active ? (
          <>
            {activeId === null ? (
              <View style={s.resumed}>
                <Text style={s.resumedText}>답을 기다리는 조건이에요</Text>
                <Pressable onPress={startNew} hitSlop={8}>
                  <Text style={s.resumedNew}>새로 등록</Text>
                </Pressable>
              </View>
            ) : null}
            <Thread cond={active} onOpen={() => router.push(`/condition/${active.id}`)} onNew={startNew} signedUp={account.signedUp} />
          </>
        ) : (
          <Intro onPick={setText} />
        )}
      </ScrollView>

      <View style={[s.dock, { paddingBottom: 12 + insets.bottom }]}>
        {!active && over && !account.signedUp ? (
          <View style={{ gap: 8 }}>
            <Text style={s.overNote}>
              지금은 <Text style={{ fontWeight: '700', color: D.ink }}>{text.trim().length}/{FREE_LIMIT}자</Text>
              {'  ·  '}가입하면 길게 적으셔도 되고 AI 가 되물어서 같이 좁혀 드려요
            </Text>
            <KakaoButton />
          </View>
        ) : null}
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
          {account.signedUp ? (
            <>
              <Text style={[s.credits, { flex: 1 }]} numberOfLines={1}>
                {account.nickname ? `${account.nickname} · ` : ''}크레딧 <Text style={{ color: D.ink, fontWeight: '700' }}>{credits ?? 0}</Text>
                <Text style={{ color: D.ink3 }}>  ·  AI 해석 3, 되묻기 1  ·  매일 +5</Text>
              </Text>
              <Pressable onPress={logout} hitSlop={8}>
                <Text style={s.logout}>로그아웃</Text>
              </Pressable>
            </>
          ) : (
            <Text style={[s.credits, { flex: 1 }]} numberOfLines={1}>
              가입 전 <Text style={{ color: D.ink, fontWeight: '700' }}>하루 1번 · {FREE_LIMIT}자</Text>
              <Text style={{ color: D.ink3 }}>  ·  가입하면 크레딧 20개</Text>
            </Text>
          )}
        </View>
        {active ? (
          <View style={s.bar}>
            <TextInput
              value={reply}
              onChangeText={setReply}
              placeholder={active.needs_confirm ? '답해 주세요' : '더 알려줄 게 있으면 적어 주세요'}
              placeholderTextColor={D.ink3}
              style={s.barInput}
              returnKeyType="send"
              onSubmitEditing={answer}
            />
            <Pressable onPress={answer} disabled={!reply.trim()} style={[s.go, !reply.trim() && { backgroundColor: D.line }]}>
              <Ionicons name="arrow-up" size={19} color="#FFFFFF" />
            </Pressable>
          </View>
        ) : (
          <View style={s.bar}>
            <TextInput
              value={text}
              onChangeText={setText}
              placeholder={`찾는 걸 적어 주세요 (${FREE_LIMIT}자까지)`}
              placeholderTextColor={D.ink3}
              style={s.barInput}
              multiline
              returnKeyType="send"
              blurOnSubmit
              onSubmitEditing={send}
            />
            <Pressable onPress={send} disabled={!ready || busy} style={[s.go, (!ready || busy) && { backgroundColor: D.line }]}>
              {busy ? <ActivityIndicator size="small" color="#FFFFFF" /> : <Ionicons name="arrow-up" size={19} color="#FFFFFF" />}
            </Pressable>
          </View>
        )}
      </View>
    </KeyboardAvoidingView>
  );
}

/** 빈 화면은 기능을 가르쳐야 한다. 예시 세 개가 이 기능의 범위를 보여 준다. */
function Intro({ onPick }: { onPick: (t: string) => void }) {
  return (
    <View style={{ gap: 16 }}>
      <View style={{ gap: 7 }}>
        <Text style={s.h1}>뭘 기다리고 계세요?</Text>
        <Text style={s.lead}>
          상품 이름을 몰라도 됩니다. 설명만 적으면 AI 가 되물어서 같이 좁히고,
          그런 제품을 파는 회사들을 전부 찾아 등록합니다.
        </Text>
      </View>
      {EXAMPLES.map((e) => (
        <Pressable key={e.text} onPress={() => onPick(e.text)} style={({ pressed }) => [s.ex, pressed && { backgroundColor: D.line }]}>
          <Text style={s.exHint}>{e.hint}</Text>
          <Text style={s.exText}>{e.text}</Text>
        </Pressable>
      ))}
    </View>
  );
}

function turnsOf(c: Condition): Condition['chat'] {
  if (c.chat?.length) return c.chat;
  return c.needs_confirm && c.question ? [{ role: 'ai', text: c.question }] : [];
}

function Thread({ cond, onOpen, onNew, signedUp }: { cond: Condition; onOpen: () => void; onNew: () => void; signedUp: boolean }) {
  const turns = turnsOf(cond) ?? [];
  const reading = !cond.parsed;
  const waiting = turns.length > 0 && turns[turns.length - 1].role === 'user';
  const blocked = cond.blocked === true;
  // AI 가 꺼져 있으면(예산 상한·장애) 규칙 파서 결과가 들어온다. 그걸 AI 해석인 척하지 않는다
  const aiOff = !reading && !blocked && !String(cond.parsed_by ?? '').startsWith('ai:');
  const { products, rules } = summarize(cond.spec as Record<string, unknown> | undefined);
  const found = (cond.candidates ?? []).map((c) => c.k);
  const shown = found.length ? found : products;

  return (
    <View style={{ gap: 14 }}>
      <View style={[s.bubble, s.mine]}>
        <Text style={s.mineText}>{cond.text}</Text>
      </View>

      {reading ? (
        <View style={[s.bubble, s.theirs, { flexDirection: 'row', alignItems: 'center', gap: 9 }]}>
          <ActivityIndicator size="small" color={D.ink3} />
          <Text style={s.theirsText}>읽는 중이에요</Text>
        </View>
      ) : null}

      {turns.map((t, i) => (
        <View key={i} style={[s.bubble, t.role === 'user' ? s.mine : s.theirs]}>
          <Text style={t.role === 'user' ? s.mineText : s.theirsText}>{t.text}</Text>
        </View>
      ))}

      {waiting ? (
        <View style={[s.bubble, s.theirs, { flexDirection: 'row', alignItems: 'center', gap: 9 }]}>
          <ActivityIndicator size="small" color={D.ink3} />
          <Text style={s.theirsText}>생각하는 중이에요</Text>
        </View>
      ) : null}

      {blocked ? (
        <View style={{ gap: 10 }}>
          <View style={[s.bubble, s.theirs, { backgroundColor: '#FBFFCE' }]}>
            <Text style={[s.theirsText, { color: D.ink }]}>{cond.parsed}</Text>
          </View>
          {!signedUp ? <KakaoButton note="가입하면 크레딧 20개. 전부 무료예요." /> : null}
        </View>
      ) : null}

      {aiOff ? (
        <View style={[s.bubble, s.theirs]}>
          <Text style={s.theirsText}>
            지금은 AI 가 읽지 못했어요. 적어 주신 말 그대로 걸어 뒀고, AI 가 돌아오면 자동으로 다시 읽습니다.
          </Text>
        </View>
      ) : null}

      {!reading && !blocked && shown.length ? (
        <View style={{ gap: 9 }}>
          <Text style={s.foundLabel}>
            {found.length ? '찾아서 등록한 제품 ' : '이 말이 들어간 딜을 찾아요 '}
            <Text style={{ color: D.ink }}>{shown.length}개</Text>
          </Text>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 7 }}>
            {shown.map((k) => (
              <View key={k} style={s.pill}>
                <Text style={s.pillText}>{k}</Text>
              </View>
            ))}
          </View>
        </View>
      ) : null}

      {!reading && !blocked && rules.length ? (
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 7 }}>
          {rules.map((r) => (
            <View key={r} style={s.rule}>
              <Text style={s.ruleText}>{r}</Text>
            </View>
          ))}
        </View>
      ) : null}

      {!reading ? (
        <View style={{ flexDirection: 'row', gap: 9, marginTop: 2 }}>
          <Pressable onPress={onOpen} style={({ pressed }) => [s.act, s.actMain, pressed && { opacity: 0.85 }]}>
            <Text style={s.actMainText}>{cond.needs_confirm ? '직접 수정' : '등록된 제품 보기'}</Text>
          </Pressable>
          <Pressable onPress={onNew} style={({ pressed }) => [s.act, pressed && { backgroundColor: D.line }]}>
            <Text style={s.actText}>새로 등록</Text>
          </Pressable>
        </View>
      ) : null}

      {cond.needs_confirm ? (
        <Text style={s.note}>아직 상품을 못 좁혀서 알림은 보류 중이에요. 답해 주시면 바로 시작합니다.</Text>
      ) : !reading && !aiOff && !blocked ? (
        <Text style={s.note}>맞는 딜이 올라오면 알림 탭으로 알려드릴게요.</Text>
      ) : null}
    </View>
  );
}

const s = StyleSheet.create({
  h1: { fontSize: 23, fontWeight: '900', lineHeight: 31, color: D.ink, letterSpacing: -0.6 },
  lead: { fontSize: 13.5, lineHeight: 21, color: D.ink2 },

  ex: { backgroundColor: D.panel, borderWidth: 1, borderColor: D.ink, paddingVertical: 12, paddingHorizontal: 14, gap: 4 },
  exHint: { fontSize: 9.5, fontWeight: '800', color: D.ink3, letterSpacing: 0.8 },
  exText: { fontSize: 14.5, lineHeight: 20, color: D.ink, fontWeight: '800' },

  bubble: { maxWidth: '88%', paddingVertical: 11, paddingHorizontal: 13, borderWidth: 1, borderColor: D.ink },
  mine: { alignSelf: 'flex-end', backgroundColor: D.ink },
  theirs: { alignSelf: 'flex-start', backgroundColor: D.panel },
  mineText: { fontSize: 14.5, lineHeight: 21, color: '#FFFFFF' },
  theirsText: { fontSize: 14.5, lineHeight: 21, color: D.ink },

  foundLabel: { fontSize: 9.5, fontWeight: '800', color: D.ink3, letterSpacing: 0.8 },
  pill: { borderWidth: 1, borderColor: D.ink, paddingVertical: 5, paddingHorizontal: 9 },
  pillText: { fontSize: 12, fontWeight: '800', color: D.ink },
  rule: { backgroundColor: D.line, paddingVertical: 5, paddingHorizontal: 9 },
  ruleText: { fontSize: 12, fontWeight: '700', color: D.ink2, fontVariant: ['tabular-nums'] },

  act: { flex: 1, height: 44, alignItems: 'center', justifyContent: 'center', backgroundColor: D.panel, borderWidth: 1, borderColor: D.ink },
  actMain: { backgroundColor: D.ink },
  actText: { fontSize: 13.5, fontWeight: '800', color: D.ink },
  actMainText: { fontSize: 13.5, fontWeight: '900', color: D.volt },
  note: { fontSize: 11.5, lineHeight: 17, color: D.ink2 },

  dock: { paddingHorizontal: 16, paddingTop: 10, gap: 8, backgroundColor: D.ground, borderTopWidth: 1, borderTopColor: D.ink },
  bar: { flexDirection: 'row', alignItems: 'flex-end', gap: 8, backgroundColor: D.panel, borderWidth: 1, borderColor: D.ink, paddingLeft: 12, paddingRight: 5, paddingVertical: 5 },
  barInput: { flex: 1, fontSize: 14.5, lineHeight: 20, color: D.ink, paddingVertical: 8, maxHeight: 110 },
  go: { width: 34, height: 34, backgroundColor: D.ink, alignItems: 'center', justifyContent: 'center' },
  credits: { fontSize: 11.5, color: D.ink2, fontVariant: ['tabular-nums'] },
  overNote: { fontSize: 11.5, lineHeight: 17, color: D.ink2 },
  logout: { fontSize: 11.5, fontWeight: '800', color: D.ink2 },
  resumed: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: -4 },
  resumedText: { fontSize: 11.5, fontWeight: '800', color: D.ink3 },
  resumedNew: { fontSize: 11.5, fontWeight: '900', color: D.ink },
});
